import VideoBitratePreset from './VideoBitratePreset';
import { multipleEncodingBitrateTable, singleEncodingBitrateTable } from './maxBitrateTables';
import getSpatialResolution from '../../helpers/getSpatialResolution';

const getPresetMaxBitrateArray = (numEncodings, resolution, preset, maxBitrate) => {
  if (preset === VideoBitratePreset.DEFAULT) {
    return Array(numEncodings).fill(maxBitrate);
  }

  if (numEncodings === 1) {
    const tableBitrate = singleEncodingBitrateTable[resolution][preset];
    if (maxBitrate !== -1) {
      // the stream is relayed
      return [Math.min(tableBitrate, maxBitrate)];
    }
    // the stream is routed
    return [tableBitrate];
  }

  const ActiveEncodingsForPreset = {
    EXTRA_BW_SAVER: 1,
    BW_SAVER: 2,
  };

  const numEncodingsForPreset = ActiveEncodingsForPreset[preset];
  const activeEncodingNum = Math.min(numEncodingsForPreset, numEncodings);

  const bitrateArray = [];
  for (let count = 0; count < numEncodings; count++) {
    if (count < activeEncodingNum) {
      bitrateArray.push(maxBitrate);
    } else {
      bitrateArray.push(0);
    }
  }
  return bitrateArray;
};

const getCustomMaxBitrateArray = (numEncodings, resolution, customBitrate, maxBitrate) => {
  const isUncapped = maxBitrate !== -1; // MANTIS is uncapped, P2P has a maxBitrate value
  let bitrateBudget = isUncapped ? Math.min(customBitrate, maxBitrate) : customBitrate;

  if (numEncodings === 1) {
    return [bitrateBudget];
  }

  // This algorrithm follows a bucket filling methodology in which the total budget
  // is progressively assigned to each encoding according to the values given in
  // multipleEncodingBitrateTable[resolution][key].
  const bitrateArray = [];
  const orderedPresets = [
    'EXTRA_BW_SAVER',
    'BW_SAVER',
    'DEFAULT',
  ];
  const bitrates = orderedPresets.map(preset => multipleEncodingBitrateTable[resolution][preset]);
  if (bitrates.length === 0) return Array(0);

  bitrates.slice(-numEncodings).forEach((value) => {
    if (value <= bitrateBudget) {
      bitrateArray.push(value);
      bitrateBudget -= value;
    } else {
      bitrateArray.push(bitrateBudget);
      bitrateBudget = 0;
    }
  });

  return bitrateArray;
};

const isVp9Codec = senderParameters => senderParameters.codecs.some(codec =>
  codec.mimeType?.toLowerCase() === 'video/vp9'
);

/*
  * This function returns an array with the maxBitrate values that correspond to
  * respectively to each of the encoding parameters of the senderParameters.
  * This array is created by getCustomMaxBitrateArray or getPresetMaxBitrateArray.
*/
const getMaxBitrateArray = (senderParameters, width, height, presetBps, maxBitrate) => {
  const originalNumEncodings = senderParameters.encodings.length;
  if (originalNumEncodings === 0) return undefined;

  const resolution = getSpatialResolution(width, height);
  if (resolution === undefined) return undefined;

  /*
   * SVC-VP9 has only one stream. However, the sender contains originalNumEncodings encodings
   * as it has been negotiated as simulcast before agreeing with the codec to use.
   * So SVC-VP9 streams will be always considered as if they were only one.
  */
  const isVp9 = isVp9Codec(senderParameters);
  const numEncodings = isVp9 ? 1 : originalNumEncodings;

  const bitrateArray = Number.isInteger(presetBps) ?
    getCustomMaxBitrateArray(numEncodings, resolution, presetBps, maxBitrate) :
    getPresetMaxBitrateArray(numEncodings, resolution, presetBps, maxBitrate);
  if (bitrateArray.length === 0) return undefined;

  if (!isVp9) {
    return bitrateArray;
  }

  const vp9MaxBitrate = bitrateArray[0];
  return Array(originalNumEncodings).fill(vp9MaxBitrate);
};

export { getMaxBitrateArray as default, isVp9Codec };
